import { Card, Divider, Typography, Breadcrumb, Alert } from 'antd';
import { useStoreActions, useStoreState } from '../../store';
import { useParams, Link } from 'react-router-dom-v5-compat';
import { useEffect } from 'react';
import { ReadmeIcon } from '../../components/assets/ReadmeIcon';
import { SHIPIUM_DOCUMENTATION_URL } from '../constants';
import { SimulationPageHeader } from './SimulationPageHeader';
import { SimulationDetailTabs } from './SimulationDetailTabs';
import { useSession } from '~/hooks/useSession';

const { Text } = Typography;

const SimulationDetailPage = () => {
  const { isShipiumView, sessionPartnerId, sessionPartnerUrl } = useSession();
  const fetchPartner = useStoreActions((actions) => actions.partner.fetchPartner);
  const fetchSimulationDetail = useStoreActions((actions) => actions.simulation.fetchSimulationDetail);
  const simulationDetails = useStoreState((state) => state.simulation.simulationDetails);
  const loading = useStoreState((state) => state.loading.loadingFlags.simulation);
  const setPageTitle = useStoreActions((actions) => actions.navigation.setPageTitle);
  const params = useParams<{ simulationId: string }>();
  const { simulationId } = params;
  const simulation = params.simulationId ? simulationDetails[params.simulationId] : undefined;

  useEffect(() => {
    if (simulationId) {
      fetchSimulationDetail(simulationId);
    }
  }, [fetchSimulationDetail, simulationId]);

  useEffect(() => {
    if (sessionPartnerId) {
      fetchPartner(sessionPartnerId);
    }
  }, [sessionPartnerId, fetchPartner]);

  useEffect(() => {
    if (simulation) {
      setPageTitle(`Simulation: ${simulation.name}`);
    } else {
      setPageTitle(`Simulation Not Found`);
    }
  }, [setPageTitle, simulation, params]);

  const detail = () => {
    if (!simulation) {
      return !loading ? (
        <Alert
          style={{ marginTop: '10px' }}
          message="Simulation Not Found"
          description={`A simulation with the id ${simulationId} could not be found.`}
          type="error"
          showIcon
        />
      ) : null;
    } else {
      return (
        <>
          <div style={{ marginTop: 8, display: 'flex' }}>
            <div style={{ marginRight: 'auto' }}>
              <Breadcrumb separator=">">
                <Breadcrumb.Item>
                  <Link to={`/${sessionPartnerUrl}`}>Home</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  <Link to={`/${sessionPartnerUrl}/simulation`}>Simulations</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  <b>
                    <Text>Simulation: {simulation?.name}</Text>
                  </b>
                </Breadcrumb.Item>
              </Breadcrumb>
            </div>
            <div style={{ alignSelf: 'end' }}>
              <a rel="noreferrer" target="_blank" href={SHIPIUM_DOCUMENTATION_URL}>
                <ReadmeIcon style={{ fontSize: '20px' }} />
              </a>
            </div>
          </div>
          <Divider orientation={'left'} style={{ marginTop: 5, marginBottom: 5 }} />
          {isShipiumView && simulation.visibleToPartner === false && (
            <Alert
              style={{ marginTop: '10px' }}
              message="Simulation is not visible to partner"
              description={`This simulation has been marked as not visible to the partner. Only Shipium users can view this simulation.`}
              type="warning"
              closable={true}
              showIcon
            />
          )}
          <SimulationPageHeader simulation={simulation} />
          <div className="card-container">
            <SimulationDetailTabs simulation={simulation} />
          </div>
        </>
      );
    }
  };

  return <Card bodyStyle={{ paddingTop: 0 }}>{detail()}</Card>;
};

export default SimulationDetailPage;
